import React, { useState, useEffect } from 'react';
import { ChartDataService } from '../../services/ChartDataService';
import { useUnifiedAuth } from '../../auth/useUnifiedAuth';
import { useSAPDataStore } from '../../store/SAPDataStore';
import { useChartCache } from '../../hooks/useChartCache';
import { useThemedCharts } from '../../hooks/useThemedCharts';
import BaseChart from './BaseChart';

interface FaultItem {
  exist_SAP: number;
  machine_sn: string;
  text: string;
  total_count: number;
}

interface NumberOfFaultsChartProps {
  machineSerialNo?: string;
  lineId?: string;
  shifts?: string[];
}

const NumberOfFaultsChart: React.FC<NumberOfFaultsChartProps> = ({ 
  machineSerialNo, 
  shifts = []
}) => {
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  const { getStatusColors, getSeriesColor } = useThemedCharts();
  const [data, setData] = useState<FaultItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use chart cache for better performance
  const chartCache = useChartCache<FaultItem[]>({
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxEntries: 30 // Cache up to 30 different chart configurations
  });

  const fetchNumberOfFaultsData = async () => {
    // Don't fetch if no shifts selected
    if (!shifts || shifts.length === 0) {
      setData(null);
      setLoading(false);
      return;
    }

    if (!machineSerialNo || !dateRange.start || !dateRange.end) return;
    if (!token) {
      setError('Authentication required');
      return;
    }

    // Check cache first
    const cachedData = chartCache.getCachedData(
      'pc.number_of_faults',
      machineSerialNo,
      dateRange,
      shifts
    );

    if (cachedData) {
      setData(cachedData);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(machineSerialNo, dateRange, shifts);
      const response = await ChartDataService.fetchNumberOfFaults(params, token);

      if (response.success && response.data) {
        const responseData = response.data as any;
        
        console.log('📊 NumberOfFaultsChart: Raw API response:', {
          success: response.success,
          responseKeys: Object.keys(responseData),
          isArray: Array.isArray(responseData),
          data: responseData
        });
        
        // Backend returns array of { exist_SAP, machine_sn, text, total_count }
        let faultsArray: FaultItem[] = [];
        
        if (Array.isArray(responseData)) {
          faultsArray = responseData;
        } else if (responseData.results && Array.isArray(responseData.results)) {
          faultsArray = responseData.results;
        } else if (responseData.number_of_faults && Array.isArray(responseData.number_of_faults)) {
          faultsArray = responseData.number_of_faults;
        }
        
        console.log('📊 NumberOfFaultsChart: Extracted faults array:', {
          count: faultsArray.length,
          sample: faultsArray.slice(0, 3)
        });
        
        setData(faultsArray);
        chartCache.setCachedData('pc.number_of_faults', machineSerialNo, dateRange, shifts, faultsArray);
      } else {
        setError('Failed to fetch number of faults data');
      }
    } catch (error) {
      console.error('Error fetching number of faults data:', error);
      setError('Error fetching number of faults data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNumberOfFaultsData();
  }, [machineSerialNo, dateRange.start, dateRange.end, shifts]);

  // Transform data for chart display
  const chartData = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { series: [], categories: [] };
    }

    // Sort by total_count descending to show highest counts first
    const sortedData = [...data].sort((a, b) => b.total_count - a.total_count);
    
    // Extract categories (fault texts) and values
    const categories = sortedData.map(item => item.text);
    const values = sortedData.map(item => item.total_count);

    console.log('📊 NumberOfFaultsChart: Processing fault data:', {
      categoriesCount: categories.length,
      categories: categories.slice(0, 5),
      values: values.slice(0, 5)
    });

    // Create single series for all faults
    const series = [{
      name: 'Fault Count',
      data: values,
      color: getSeriesColor(0)
    }];

    return {
      series,
      categories
    };
  }, [data, getSeriesColor]);

  // Create custom options similar to DowntimeChart
  const customOptions = React.useMemo(() => {
    return {
      yAxis: {
        opposite: true, // This moves the Y-axis (values) to the top for horizontal bar charts
        title: {
          text: 'Number of Faults',
          style: {
            fontSize: '14px',
            fontWeight: '500',
            color: getStatusColors.yAxisTitle
          }
        },
        labels: {
          style: {
            fontSize: '12px',
            color: getStatusColors.yAxisLabels
          }
        }
      },
      tooltip: {
        formatter: function(this: any) {
          const point = this.point;
          const value = point.y || 0;
          const category = chartData.categories[point.x] || point.category || '';
          
          return `<b>${category}</b><br/>Count: <strong>${value.toLocaleString()} fault${value !== 1 ? 's' : ''}</strong>`;
        }
      }
    };
  }, [chartData, getStatusColors.yAxisTitle, getStatusColors.yAxisLabels]);

  // Show loading state in BaseChart when loading (check BEFORE shifts check)
  if (loading) {
    return (
      <div className="w-full">
        <BaseChart
          type="bar"
          height={400}
          categories={[]}
          series={[]}
          loading={true}
        />
      </div>
    );
  }

  if (!shifts || shifts.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">No Shifts Selected</h3>
          <p className="theme-text-secondary">No shifts selected or available for the selected timeframe</p>
        </div>
      </div>
    );
  }

  // Check for data - now using flat array format
  const hasFaultsData = data && Array.isArray(data) && data.length > 0;
  
  if (!hasFaultsData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">No Faults Data</h3>
          <p className="theme-text-secondary">No fault events found for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BaseChart
        commentProps={{ chartType: 'number_of_faults', machineBK: machineSerialNo }}
        type="bar"
        height={Math.max(400, chartData.categories.length * 40 + 100)}
        categories={chartData.categories}
        series={chartData.series}
        yAxis={{
          title: 'Number of Faults',
          min: 0,
          format: '{value}'
        }}
        customOptions={customOptions}
        legend={{
          enabled: true,
          position: 'bottom'
        }}
        loading={loading}
        error={error}
        className="w-full"
      />
    </div>
  );
};

export default NumberOfFaultsChart;
